import React, { Component } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { capitalize } from 'lodash';

const defaultTransitions = {
  width: '200ms',
  height: '200ms',
  borderRadius: '1000ms',
};

export default class PointsDisplay extends Component {
  constructor(props) {
    super(props);

    const { points, tiers, labels } = this.props;
    const ranks = {};
    tiers?.forEach((tier, i) => {
      ranks[tier] = labels[i];
    });

    this.state = {
      progress: null,
      points,
      ranks,
    };
  }

  componentDidMount() {
    this.calculateProgress();
  }

  componentDidUpdate(prevProps, prevState) {
    this.recalculateValues(prevProps, prevState);
  }

  getTieredValue(array = null, pointsOverride = null) {
    const { tiers, points: pointsProp } = this.props;
    let index = 0;
    tiers?.forEach((points, i) => {
      // Could be improved, maybe using filter / reduce?
      if ((pointsOverride || pointsProp) > points && index !== i) {
        index = i;
      }
    });
    return array ? array[index < 0 ? 0 : index] : index;
  }

  getStyleObject() {
    const { tierColors, textColors } = this.props;
    const style = {};

    const backgroundColor = tierColors.length > 0 && this.getTieredValue(tierColors);
    const color = textColors.length > 0 && this.getTieredValue(textColors);

    if (backgroundColor) style.backgroundColor = backgroundColor;
    if (color) style.color = color;

    return style;
  }

  recalculateValues(prevProps, prevState) {
    const { points } = this.props;
    const { points: pointState } = this.state;
    const { points: prevPointsProp } = prevProps;
    const { points: prevPointsState } = prevState;

    const changed = prevPointsProp !== points || prevPointsState !== points;
    if (changed && pointState !== points) {
      this.setState({ points }, () => {
        this.calculateProgress(points);
      });
    }
  }

  calculateProgress(newPoints = 0) {
    const { points: oldPoints, tiers, segmented } = this.props;
    const points = newPoints || oldPoints;

    let newProgress = 0;
    let current = 0;

    // Calculate till closest tier based on points
    tiers.forEach((tier, i) => {
      if (points >= tier) {
        current = i;
      }
    });
    
    current = current < 0 ? 0 : current + 1;
    newProgress = (current / tiers.length) * 100;

    if (current === tiers.length) {
      this.setState({
        progress: 100,
      });
      return;
    }

    // Calculate progress between tiers
    const currentTier = tiers[current - 1];
    const nextTier = tiers[current];
    const pointsBetweenTiers = nextTier - currentTier;
    const pointsToNextTier = points - currentTier;
    const progressBetweenTiers = pointsToNextTier / pointsBetweenTiers;
    const percentageBetweenTiers = progressBetweenTiers;
    newProgress += (100 / tiers.length) * percentageBetweenTiers;
    
    if (newProgress <= 8.5) {
      newProgress = 0;
    }

    this.setState({
      progress: newProgress,
    });
  }

  render() {
    const { tiers, segmented, orientation, transitions, onClick, startingValue, minSize } = this.props;
    const { points: pointState, ranks } = this.state;
    let { progress } = this.state;

    const currentTier = tiers && this.getTieredValue();
    const nextTier = tiers && (currentTier + 1 < tiers.length ? currentTier + 1 : tiers.length - 1);

    const mergedTransitions = { ...defaultTransitions, ...transitions };

    const transition = {
      transition: Object.keys(mergedTransitions)
        .map((key) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()} ${mergedTransitions[key]}`)
        .join(', '),
    };

    const key = orientation === 'horizontal' ? 'width' : 'height';
    const wrapper = document.querySelector('.wrapper__bar');
    const value = +wrapper && wrapper[`client${capitalize(key)}`] / tiers.length;

    const overlayStyle = {
      [key]: `${
        progress
          ? Math.floor(progress)
          : Math.floor((startingValue / (segmented ? tiers[tiers.length - 1] : 1)) * (segmented ? 100 : 1))
      }%`,
    };

    const wrapperStyle = minSize ? { [`min${capitalize(key)}`]: minSize } : {};
    const points = pointState >= tiers[tiers.length - 1] ? tiers[tiers.length - 1] : pointState;
    const styleObject = this.getStyleObject();

    return (
      <button
        type="button"
        className={clsx('wrapper__bar', {
          horizontal: orientation === 'horizontal',
          vertical: orientation === 'vertical',
        })}
        style={{ ...wrapperStyle, ...transition }}
        onClick={onClick}
      >
        {segmented &&
          tiers.map((tier, i) => (
            <div
              key={tier}
              className={clsx('progress__bar', orientation, {
                start: i === 0,
                end: i === tiers.length - 1,
              })}
              style={styleObject}
            >
              <span>{ranks[tier]}</span>
            </div>
          ))}
        {!segmented && (
          <div className="progress__bar full" style={styleObject}>
            <span className={clsx('progress__info', orientation)}>
              <span>{i18n.translate('points.outOf', { points })}</span>
              <span>{`${tiers[nextTier]} ${i18n.translate('points.unit', { points })}`}</span>
            </span>
          </div>
        )}
        <div
          className={clsx('progress__overlay', orientation, { end: Math.floor(progress) >= 95 })}
          style={{ ...overlayStyle, ...transition }}
        >
          {segmented && tiers.map((tier) => <span key={tier} style={{ flex: `0 0 ${value}px` }} />)}
          {!segmented && (
            <span
              className={clsx('progress__info full', orientation)}
              style={{ flex: `0 0 ${value * tiers.length}px` }}
            />
          )}
        </div>
      </button>
    );
  }
}

PointsDisplay.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string),
  minSize: PropTypes.string,
  onClick: PropTypes.func,
  orientation: PropTypes.string,
  points: PropTypes.number,
  segmented: PropTypes.bool,
  startingValue: PropTypes.number,
  textColors: PropTypes.arrayOf(PropTypes.string),
  tierColors: PropTypes.arrayOf(PropTypes.string),
  tiers: PropTypes.arrayOf(PropTypes.number),
  transitions: PropTypes.shape(),
};

PointsDisplay.defaultProps = {
  labels: [],
  minSize: null,
  onClick: null,
  orientation: 'horizontal',
  points: 0,
  segmented: false,
  startingValue: 0,
  textColors: [],
  tierColors: [],
  tiers: [0],
  transitions: defaultTransitions,
};
